import type {
    AccountInfoResponse,
    ClientStats,
    UpsertUserAccountAttribute,
} from '~/@types/backend-types'
import { mutateSetProfileName } from '~/query/app.mutations'
import type { Dispatch } from '~/state/common/actions'
import {
    AccountAttributesStatus,
    setAccountAttribute,
    setAccountAttributes,
    setAccountAttributesError,
    setAccountAttributesStatus,
} from '~/state/currentUser/accountAttributesSlice'
import {
    FetchedAccountInfo,
    FetchedFileCount,
    FetchedFullScreenCards,
    StartFetchingAccountInfo,
    UnableToFetchAccountInfo,
} from '~/state/currentUser/actions'
import { dismissFullScreenCardModal } from '~/state/modalManager/modalManagerSlice'
import {
    ProfileNameChangeFailed,
    ProfileNameChanged,
    ProfilePictureChangeFailed,
    ProfilePictureChangeStarted,
} from '~/state/users/actions'
import { handleUnknownError } from '~/utilities/errorUtils'

import type { PostRequestUserStatisticsEventsBodiesEventsItem } from '@capture/client-api/src/orval'

import { clearLoginInfo } from './externals'
import { getAppService } from './SessionServiceProvider'

export const fetchAccountInfo = async (dispatch: Dispatch): Promise<AccountInfoResponse> => {
    try {
        dispatch(StartFetchingAccountInfo())
        const service = getAppService()
        const accountInfo = await service.getAccountInfo()
        dispatch(
            FetchedAccountInfo({
                allowAnalytics: accountInfo.permitted_services?.analytics ?? false,
                file_type_counters: accountInfo.file_type_counters,
                isBeingShutDown: Boolean(accountInfo.shutdown_info),
                is_read_only_user: accountInfo.is_read_only_user ?? false,
                logged_in_as: accountInfo.logged_in_as,
                max_space: parseInt(accountInfo.max_space, 10),
                name: accountInfo.name,
                number_of_subscribed_albums: accountInfo.number_of_subscribed_albums ?? 0,
                profile_picture: accountInfo.profile_picture_url ?? undefined,
                sunset_delete_date: accountInfo.sunset_delete_date,
                tos_to_approve: accountInfo.tos_to_approve ?? null,
                used_space: parseInt(accountInfo.used_space, 10),
                uuid: accountInfo.uuid,
                hasUnlimitedQuota: Boolean(accountInfo.has_unlimited_quota),
            }),
        )
        return accountInfo
    } catch (e) {
        clearLoginInfo()
        dispatch(UnableToFetchAccountInfo())
        throw e
    }
}

export const setProfileUserName = async (dispatch: Dispatch, name: string) => {
    try {
        await mutateSetProfileName(name)
        dispatch(ProfileNameChanged({ name }))
    } catch (e) {
        console.error('Failed to set profile username:', e)
        dispatch(ProfileNameChangeFailed())
    }
}

export const setProfilePicture = async (dispatch: Dispatch, file: Blob) => {
    try {
        const service = getAppService()
        dispatch(ProfilePictureChangeStarted())
        await service.setProfilePictureFromBlob(file)
        await fetchAccountInfo(dispatch)
    } catch (e) {
        console.error('Failed to set profile picture:', e)
        dispatch(ProfilePictureChangeFailed())
    }
}

export const refreshFileCount = async (dispatch: Dispatch, timelineId?: JobID) => {
    try {
        const service = getAppService()
        const { file_type_counters } = await fetchAccountInfo(dispatch)

        // CAPWEB-3121: remove getTimelineMonths call when getAccountInfo response includes `document_count`
        const { months } = await service.getTimelineMonths(
            timelineId ?? (await service.getDefaultJob()).id,
        )

        const documentCount = months.reduce((count, month) => count + month.document_count, 0)
        const countersWithDocs = {
            ...file_type_counters,
            document_count: documentCount,
        }
        dispatch(
            FetchedFileCount({
                file_type_counters: countersWithDocs,
            }),
        )
    } catch (e) {
        console.error('Failed to refresh file count:', e)
    }
}

export const sendUserStatistics = async (
    events: PostRequestUserStatisticsEventsBodiesEventsItem[],
) => {
    try {
        const service = getAppService()
        await service.postUserStatistics(events)
    } catch (e) {
        console.error('Failed to send user statistics:', e)
    }
}

export const sendClientStats = async (data: ClientStats) => {
    try {
        const service = getAppService()
        await service.postClientStats(data)
    } catch (e) {
        console.error('Failed to send client stats:', e)
    }
}

export const fetchAccountAttributes = async (dispatch: Dispatch): Promise<void> => {
    try {
        dispatch(setAccountAttributesStatus(AccountAttributesStatus.FETCHING))
        const service = getAppService()
        const result = await service.getAccountAttributes()
        dispatch(setAccountAttributes(result))
        dispatch(setAccountAttributesStatus(AccountAttributesStatus.SUCCEEDED))
    } catch (error) {
        let errorMessage = 'unknown Error'
        if (typeof error === 'string') {
            errorMessage = error
        } else if (error instanceof Error) {
            errorMessage = error.message
        }
        dispatch(setAccountAttributesStatus(AccountAttributesStatus.FAILED))
        dispatch(setAccountAttributesError(errorMessage))
    }
}

export const upsertAccountAttributes = async (
    options: UpsertUserAccountAttribute,
    dispatch: Dispatch,
) => {
    try {
        dispatch(setAccountAttributesStatus(AccountAttributesStatus.FETCHING))
        const service = getAppService()
        const result = await service.upsertAccountAttributes(options)
        dispatch(setAccountAttribute(result[0]))
        dispatch(setAccountAttributesStatus(AccountAttributesStatus.SUCCEEDED))
    } catch (error) {
        const errorMessage = handleUnknownError(error)
        dispatch(setAccountAttributesStatus(AccountAttributesStatus.FAILED))
        dispatch(setAccountAttributesError(errorMessage))
    }
}

/**
 * Fetches and prepares full screen cards for displaying
 * Cards are filtered and sorted by descending priority
 * - Filter for backend_controlled_cards
 * - Filter for the only currently supported template `generic_message_1`
 */
export const fetchFullScreenCards = async (dispatch: Dispatch) => {
    const service = getAppService()
    const fullscreenCardResponse = await service.getFullScreenCards()

    const cards = fullscreenCardResponse.backend_controlled_cards
        .filter((card) => card.template === 'generic_message_1')
        .sort((a, b) => b.priority - a.priority)

    dispatch(FetchedFullScreenCards(cards))
}

/**
 * Calls the dismiss endpoint for a given fullscreen card Id
 * and after dismisses the modal from the local state
 */
export const dismissFullScreenCard = async (cardId: string, dispatch: Dispatch): Promise<void> => {
    try {
        const service = getAppService()
        await service.dismissFullScreenCard(cardId)
    } catch (error) {
        console.error('Error dismissing fullscreen card:', error)
    } finally {
        dispatch(dismissFullScreenCardModal())
    }
}
